/**
 * The sidebar project picker's contract — types and the two pure rules both
 * sides must agree on. No database, no DOM: the client bundles this file.
 *
 * Design: `Led AI - Project Picker.dc.html` (Claude Design project
 * `806e74fa-5b1c-4c5c-bd8e-4d71ff80a106`). The picker is built for workspaces
 * with hundreds of projects, so the browser never holds the whole list: it
 * asks `GET /api/project-picker` for one page at a time, searches on the
 * server, and asks for the next page when the reader scrolls near the end.
 */

/** One row in the picker. */
export interface PickerProject {
  id: string;
  /** `""` when the site has no name; the client prints the id instead. */
  name: string;
  /** Every walk (visit) recorded on this project. */
  walks: number;
}

/** A letter header's count, for the A–Z section. */
export interface PickerLetter {
  letter: string;
  count: number;
}

export interface ProjectPickerPage {
  items: PickerProject[];
  /** Every project in the workspace, matching or not. */
  total: number;
  /** Projects matching the query; equals `total` when there is no query. */
  matchCount: number;
  /** Offset of the next page, or null when this page is the last. */
  nextOffset: number | null;
  /**
   * Per-letter counts over the WHOLE workspace, so a letter header can say
   * how many projects sit under it before they have all been fetched. Sent
   * only with the first unfiltered page — a search shows one match list and
   * no letter headers.
   */
  letters?: PickerLetter[];
}

export interface ProjectPickerByIdResponse {
  /** In the order the ids were asked for; ids outside the workspace are dropped. */
  items: PickerProject[];
}

export const PICKER_PAGE_SIZE = 40;
export const PICKER_MAX_PAGE_SIZE = 100;
export const PICKER_MAX_QUERY_LENGTH = 120;
/** How many recent projects the picker remembers and may look up at once. */
export const PICKER_MAX_IDS = 8;

/**
 * The letter header a project sorts under: its first letter, accents folded
 * (`É` files under `E`), or `#` for anything that does not start with A–Z.
 * The server counts with this and the client groups with it, so a header's
 * count always describes the rows printed beneath it.
 */
export function letterOf(name: string): string {
  const first = name.trim().normalize("NFD").replace(/[̀-ͯ]/g, "").charAt(0).toUpperCase();
  return first >= "A" && first <= "Z" ? first : "#";
}

/**
 * Split a name around the first case-insensitive occurrence of `query`, so
 * the matched text can be drawn bold. `hit` is empty when nothing matches.
 */
export function splitMatch(name: string, query: string): { pre: string; hit: string; post: string } {
  const needle = query.trim().toLowerCase();
  const at = needle ? name.toLowerCase().indexOf(needle) : -1;
  if (at < 0) return { pre: name, hit: "", post: "" };
  return {
    pre: name.slice(0, at),
    hit: name.slice(at, at + needle.length),
    post: name.slice(at + needle.length),
  };
}
